import { HTMLAttributes, RefObject, useEffect, useRef } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { TextLink } from "../../../mdx-pages/components/content/link";
import { LinksBlock } from "../../../shared/components/layout/footer/components/LinksBlock";
import { ScrollButton } from "../../../shared/components/link/ScrollButton";
import { responsiveIconSizes } from "../../../shared/styles/responsive.css";
import { getUrl } from "../../../shared/utils/getUrl";
import {
  COST_YOUR_PROJECT_PAGE,
  JOIN_OUR_MAILING_LIST_PAGE,
  LEGAL_NOTICE_PAGE,
  PRIVACY_POLICY_PAGE,
} from "../../../providers/routing/routes";
import { useLanguage } from "../../../providers/language/context";
import { useInView } from "motion/react";
import { removeHash, replaceHash } from "../../../providers/routing/utils";
import {
  footer,
  footerBottomContainer,
  footerCopyrightContainer,
  footerCopyrightText,
  footerFormContainer,
  footerMainContainer,
  footerScrollUpContainer,
} from "./styles.css";
import classNames from "classnames";

type FooterProps = {
  onScrollToTop?: () => void;
  className?: string;
} & Pick<HTMLAttributes<HTMLDivElement>, "id">;

const CopyRightsLayout = ({ text }: { text: string }) => {
  return (
    <div className={footerCopyrightText}>
      {text
        .split(". ")
        .map((element: string, index: number, array: Array<string>) => (
          <span key={element}>
            {element + (index === array.length - 1 ? "" : ". ")}
          </span>
        ))}
    </div>
  );
};

export const Footer = ({ onScrollToTop, className }: FooterProps) => {
  const { language } = useLanguage();
  const footerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(footerRef, { amount: 0.9 });
  const footerItem = useStaticQuery(graphql`
    query {
      allPagesJson {
        nodes {
          title
          language
          fields {
            bodyParsed {
              title
              signUpLink
              costProjectLink
              updates
              rights
              checkOut
              legalNoticeLink
              privacyPolicyLink
            }
          }
        }
      }
    }
  `);

  const contentArray = footerItem.allPagesJson.nodes;

  const footerContent =
    contentArray.find(
      (i: Record<string, string | number>) =>
        i?.language === language && i?.title === "Footer",
    ) ??
    contentArray.find(
      (i: Record<string, string | number>) =>
        i?.language === "en" && i?.title === "Footer",
    );

  const footerBody =
    footerContent &&
    typeof footerContent === "object" &&
    footerContent.fields.bodyParsed
      ? footerContent.fields.bodyParsed
      : {};

  const {
    title = "",
    signUpLink = "",
    costProjectLink = "",
    updates = "",
    rights = "",
    checkOut = "",
    legalNoticeLink = "",
    privacyPolicyLink = "",
  } = footerBody;

  useEffect(() => {
    if (isInView) {
      replaceHash("footer");
    } else {
      removeHash();
    }
  }, [isInView]);

  return (
    <div
      ref={footerRef}
      id={"footer"}
      className={classNames(footer, className)}
    >
      <div className={footerFormContainer}>
        <div className={footerMainContainer}>
          <span>{title}</span>
          <span>
            <TextLink to={getUrl(language, JOIN_OUR_MAILING_LIST_PAGE)}>
              {signUpLink}
            </TextLink>{" "}
            {updates}
            <TextLink to={getUrl(language, COST_YOUR_PROJECT_PAGE)}>
              {costProjectLink}
            </TextLink>
          </span>
        </div>
      </div>
      <div className={footerBottomContainer}>
        <LinksBlock />
        <div className={footerCopyrightContainer}>
          <CopyRightsLayout text={rights} />
          {checkOut}
          <TextLink to={getUrl(language, LEGAL_NOTICE_PAGE)}>
            {legalNoticeLink}
          </TextLink>
          {language === "en" ? " and " : " "}
          <TextLink to={getUrl(language, PRIVACY_POLICY_PAGE)}>
            {privacyPolicyLink}
          </TextLink>
        </div>
      </div>
      <div className={footerScrollUpContainer}>
        <ScrollButton
          className={responsiveIconSizes}
          direction="up"
          onClick={onScrollToTop}
        />
      </div>
    </div>
  );
};
